/**
 * Offer CAP timeframes (MD-1147 / MD-1269) — the window a partner cap counts
 * over, shared by the counters, the DTOs, the name parser and every label.
 * A day, week or month cap counts over the DANISH calendar period (the
 * partners reset at 00:00 Danish time — day: every midnight, week: Monday,
 * month: the 1st); lifetime is the offer's total. Legacy NULL and unknown
 * values read as lifetime, the form's default.
 * For Media Devoted | Created By James Alexandrov
 */

export const CAP_TIMEFRAMES = ['day', 'week', 'month', 'lifetime'] as const;
export type CapTimeframe = (typeof CAP_TIMEFRAMES)[number];

/** The calendar-period timeframes — everything but lifetime. */
export type CapPeriod = Exclude<CapTimeframe, 'lifetime'>;
export const CAP_PERIODS: readonly CapPeriod[] = ['day', 'week', 'month'];

export const isCapTimeframe = (value: unknown): value is CapTimeframe =>
  typeof value === 'string' && (CAP_TIMEFRAMES as readonly string[]).includes(value);

/** A stored value as a timeframe: NULL (legacy rows) and anything unknown
 *  read as lifetime. */
export const normalizeCapTimeframe = (raw: string | null | undefined): CapTimeframe =>
  isCapTimeframe(raw) ? raw : 'lifetime';

export const CAP_TIMEFRAME_LABELS: Record<CapTimeframe, string> = {
  day: 'Per day',
  week: 'Per week',
  month: 'Per month',
  lifetime: 'Lifetime',
};

export const capTimeframeLabel = (raw: string | null | undefined): string =>
  CAP_TIMEFRAME_LABELS[normalizeCapTimeframe(raw)];

/** The window in words, for tooltips — says when the count resets. */
export const CAP_TIMEFRAME_NOTES: Record<CapTimeframe, string> = {
  day: 'per day (resets 00:00 Danish time)',
  week: 'per week (Mon–Sun, resets Monday 00:00 Danish time)',
  month: 'per calendar month (resets on the 1st, 00:00 Danish time — a reached cap stays paused until you confirm the new cap)',
  lifetime: 'lifetime',
};

export const capTimeframeNote = (raw: string | null | undefined): string =>
  CAP_TIMEFRAME_NOTES[normalizeCapTimeframe(raw)];
